import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookHeart, Check, X, Trash2, Edit2, Eye, Clock, CheckCircle, XCircle, Loader2, Mail, Phone, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminStories() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedStory, setSelectedStory] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [processing, setProcessing] = useState(null);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stories/all`);
      if (response.ok) {
        const data = await response.json();
        setStories(data);
      }
    } catch (error) {
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStories();
  }, []);

  const handleApprove = async (storyId) => {
    setProcessing(storyId);
    try {
      const response = await fetch(`${API_URL}/api/stories/${storyId}/approve`, {
        method: 'PUT'
      });
      if (response.ok) {
        toast.success('Story approved and published!');
        fetchStories();
        setSelectedStory(null);
      }
    } catch (error) {
      toast.error('Failed to approve story');
    } finally {
      setProcessing(null);
    }
  };

  const handleDeny = async (storyId) => {
    setProcessing(storyId);
    try {
      const response = await fetch(`${API_URL}/api/stories/${storyId}/deny`, {
        method: 'PUT'
      });
      if (response.ok) {
        toast.success('Story denied');
        fetchStories();
        setSelectedStory(null);
      }
    } catch (error) {
      toast.error('Failed to deny story');
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    
    setProcessing(storyId);
    try {
      const response = await fetch(`${API_URL}/api/stories/${storyId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Story deleted');
        fetchStories();
        setSelectedStory(null);
      }
    } catch (error) {
      toast.error('Failed to delete story');
    } finally {
      setProcessing(null);
    }
  };

  const handleUpdate = async () => {
    if (!selectedStory) return;
    
    setProcessing(selectedStory.id);
    try {
      const response = await fetch(`${API_URL}/api/stories/${selectedStory.id}?name=${encodeURIComponent(editData.name)}&location=${encodeURIComponent(editData.location)}&story=${encodeURIComponent(editData.story)}&category=${encodeURIComponent(editData.category)}`, {
        method: 'PUT'
      });
      if (response.ok) {
        toast.success('Story updated');
        fetchStories();
        setEditing(false);
      }
    } catch (error) {
      toast.error('Failed to update story');
    } finally {
      setProcessing(null);
    }
  };

  const filteredStories = stories.filter(s => {
    if (filter === 'all') return true;
    return s.status === filter;
  });

  const pendingCount = stories.filter(s => s.status === 'pending').length;
  const approvedCount = stories.filter(s => s.status === 'approved').length;
  const deniedCount = stories.filter(s => s.status === 'denied').length;

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800'
    };
    const icons = {
      pending: <Clock size={12} />,
      approved: <CheckCircle size={12} />,
      denied: <XCircle size={12} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-stories-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-serif text-charcoal flex items-center gap-3">
            <BookHeart className="text-burgundy" />
            Impact Stories
          </h1>
          <p className="text-charcoal/60 text-sm mt-1">
            Review and approve community stories
          </p>
        </div>
        <button
          onClick={fetchStories}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cream hover:bg-gold/20 transition-colors text-sm"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-amber-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
          <div className="text-xs text-amber-800">Pending Review</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{approvedCount}</div>
          <div className="text-xs text-green-800">Published</div>
        </div>
        <div className="bg-red-50 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{deniedCount}</div>
          <div className="text-xs text-red-800">Denied</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gold/10 pb-4">
        {['pending', 'approved', 'denied', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f 
                ? 'bg-burgundy text-white' 
                : 'bg-cream text-charcoal hover:bg-gold/20'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'pending' && pendingCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Stories List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-burgundy" size={32} />
        </div>
      ) : filteredStories.length === 0 ? (
        <div className="text-center py-20 bg-cream/50 rounded-2xl">
          <BookHeart size={48} className="mx-auto text-charcoal/30 mb-4" />
          <p className="text-charcoal/60">No {filter === 'all' ? '' : filter} stories found</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredStories.map((story) => (
            <motion.div
              key={story.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gold/10 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                {/* Image */}
                {story.image_url && (
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-cream">
                    <img 
                      src={story.image_url} 
                      alt={story.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-semibold text-charcoal">{story.name}</h3>
                    <span className="text-sm text-charcoal/50">• {story.location}</span>
                    {getStatusBadge(story.status)}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 text-xs text-charcoal/50 mb-3">
                    <span className="px-2 py-1 bg-cream rounded">{story.category}</span>
                    <span>{formatDate(story.created_at)}</span>
                  </div>

                  <p className="text-sm text-charcoal/70 line-clamp-2">
                    {story.story}
                  </p>

                  {/* Contact Info */}
                  {(story.email || story.phone) && (
                    <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-charcoal/50">
                      {story.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={12} />
                          {story.email}
                        </span>
                      )}
                      {story.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} />
                          {story.phone}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 lg:flex-col">
                  <button
                    onClick={() => {
                      setSelectedStory(story);
                      setEditData(story);
                      setEditing(false);
                    }}
                    className="p-2 rounded-lg hover:bg-cream transition-colors"
                    title="View Details"
                  >
                    <Eye size={18} className="text-charcoal/50" />
                  </button>
                  
                  {story.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(story.id)}
                        disabled={processing === story.id}
                        className="p-2 rounded-lg bg-green-100 hover:bg-green-200 transition-colors disabled:opacity-50"
                        title="Approve"
                      >
                        {processing === story.id ? (
                          <Loader2 size={18} className="animate-spin text-green-600" />
                        ) : (
                          <Check size={18} className="text-green-600" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeny(story.id)}
                        disabled={processing === story.id}
                        className="p-2 rounded-lg bg-red-100 hover:bg-red-200 transition-colors disabled:opacity-50"
                        title="Deny"
                      >
                        <X size={18} className="text-red-600" />
                      </button>
                    </>
                  )}
                  
                  <button
                    onClick={() => handleDelete(story.id)}
                    disabled={processing === story.id}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Story Detail Modal */}
      {selectedStory && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => { setSelectedStory(null); setEditing(false); }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gold/10 p-4 flex items-center justify-between">
              <h3 className="font-serif text-lg text-charcoal">Story Details</h3>
              <button
                onClick={() => { setSelectedStory(null); setEditing(false); }}
                className="p-2 rounded-lg hover:bg-cream transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {editing ? (
                // Edit Form
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name</label>
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Location</label>
                    <input
                      type="text"
                      value={editData.location}
                      onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <input
                      type="text"
                      value={editData.category}
                      onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Story</label>
                    <textarea
                      rows={6}
                      value={editData.story}
                      onChange={(e) => setEditData({ ...editData, story: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <button
                      onClick={() => setEditing(false)}
                      className="px-4 py-2 rounded-lg text-sm text-charcoal/70 hover:bg-cream transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdate}
                      disabled={processing}
                      className="px-4 py-2 rounded-lg text-sm bg-burgundy text-white hover:bg-purple transition-colors disabled:opacity-50"
                    >
                      {processing ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <>
                  <div className="flex items-start gap-4 mb-6">
                    {selectedStory.image_url && (
                      <img 
                        src={selectedStory.image_url} 
                        alt={selectedStory.name}
                        className="w-24 h-24 rounded-xl object-cover"
                      />
                    )}
                    <div>
                      <h4 className="text-xl font-semibold text-charcoal">{selectedStory.name}</h4>
                      <p className="text-charcoal/60">{selectedStory.location}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-2 py-1 bg-cream rounded text-xs">{selectedStory.category}</span>
                        {getStatusBadge(selectedStory.status)}
                      </div>
                    </div>
                  </div>

                  <div className="bg-cream/50 rounded-xl p-4 mb-6">
                    <p className="text-charcoal/80 whitespace-pre-wrap">{selectedStory.story}</p>
                  </div>

                  {(selectedStory.email || selectedStory.phone) && (
                    <div className="flex flex-wrap gap-4 mb-6 text-sm">
                      {selectedStory.email && (
                        <a href={`mailto:${selectedStory.email}`} className="flex items-center gap-2 text-burgundy hover:underline">
                          <Mail size={16} />
                          {selectedStory.email}
                        </a>
                      )}
                      {selectedStory.phone && (
                        <a href={`tel:${selectedStory.phone}`} className="flex items-center gap-2 text-burgundy hover:underline">
                          <Phone size={16} />
                          {selectedStory.phone}
                        </a>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-charcoal/50 mb-6">
                    Submitted: {formatDate(selectedStory.created_at)}
                    {selectedStory.approved_at && (
                      <span className="ml-4">Approved: {formatDate(selectedStory.approved_at)}</span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gold/10">
                    <button
                      onClick={() => setEditing(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cream hover:bg-gold/20 transition-colors text-sm"
                    >
                      <Edit2 size={16} />
                      Edit
                    </button>
                    
                    {selectedStory.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(selectedStory.id)}
                          disabled={processing}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-100 hover:bg-green-200 text-green-800 transition-colors text-sm disabled:opacity-50"
                        >
                          <Check size={16} />
                          Approve & Publish
                        </button>
                        <button
                          onClick={() => handleDeny(selectedStory.id)}
                          disabled={processing}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-800 transition-colors text-sm disabled:opacity-50"
                        >
                          <X size={16} />
                          Deny
                        </button>
                      </>
                    )}
                    
                    <button
                      onClick={() => handleDelete(selectedStory.id)}
                      disabled={processing}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors text-sm disabled:opacity-50 ml-auto"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
